/**
 * OMD-160 — 뷰어 본문 복사 시 인라인 이미지 유지.
 *
 * InnerViewer 본문은 host DOM 에 렌더되고 인라인 `<img>` 는 host 에서 만든 `blob:` URL 을 src 로
 * 갖는다. 이 상태로 복사하면 편집기(NgwEditor)가 `blob:` 를 `fetch` 하지 못하고(스킴 제약/revoke),
 * ProseMirror image parseDOM 정규식(`^(?:https?:|data:image\/)`)이 `blob:` 를 schema 에서 drop 해
 * 이미지가 소실된다. 따라서 복사 시 `blob:` src 를 self-contained `data:` URL 로 치환해야 한다.
 *
 * 비동기 변환(XHR/FileReader)은 copy 이벤트에서 `await` 후 `clipboardData` 쓰기가 무효화될 수
 * 있으므로, 선택 시작 시점에 미리 변환해 둔 `blob url → data URL` 맵을 복사 시 **동기** 치환한다.
 * 본 모듈은 이벤트에서 분리된 순수/헬퍼 함수만 담아 단위 테스트를 단순화한다.
 */

import { composedContains } from 'utils/core'

/**
 * `blob:` URL 을 `data:` URL 로 변환. same-realm 에서 생성된 blob 이라 XHR 로 접근 가능
 * (`fetch(blob:)` 는 실패할 수 있어 XHR 사용 — helper/mailview.ts `_imgToBase64` 와 동일 근거).
 * 실패 시 null 을 돌려 호출부가 폴백하도록 한다.
 */
export function blobUrlToDataUrl(url: string): Promise<string | null> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest()
    xhr.open('GET', url, true)
    xhr.responseType = 'blob'
    // blob: URL 은 메모리 참조라 즉시 완료 — 5초면 충분.
    xhr.timeout = 5_000
    xhr.onload = function () {
      if (this.status !== 200) {
        resolve(null)
        return
      }
      const reader = new FileReader()
      reader.onloadend = () => {
        const data = reader.result
        resolve(typeof data === 'string' && data.indexOf('data:') === 0 ? data : null)
      }
      reader.onerror = () => resolve(null)
      reader.readAsDataURL(this.response as Blob)
    }
    xhr.onerror = () => resolve(null)
    xhr.ontimeout = () => resolve(null)
    xhr.send()
  })
}

/**
 * 선택 영역의 시작/끝 노드가 모두 주어진 본문 컨테이너(들) 안에 있는지. 본문 밖 선택이나 컨테이너를
 * 걸친 선택은 false 를 돌려 기본 복사에 위임(호스트 UI 복사와 격리).
 */
export function isSelectionInsideBody(sel: Selection | null, containers: HTMLElement[]): boolean {
  if (sel == null || sel.rangeCount === 0 || containers.length === 0) return false
  const inAny = (node: Node | null): boolean =>
    node != null && containers.some((container) => container.contains(node))
  return inAny(sel.anchorNode) && inAny(sel.focusNode)
}

type ComposedRangesSelection = Selection & {
  getComposedRanges?: (...args: unknown[]) => StaticRange[]
}

// 표준은 `getComposedRanges({ shadowRoots })`, 구 Safari 는 `getComposedRanges(...shadowRoots)` 다.
// 시그니처가 맞지 않으면 TypeError 이므로 표준 형태부터 시도한다.
function readComposedRange(sel: Selection, shadowRoots: ShadowRoot[]): StaticRange | null {
  const fn = (sel as ComposedRangesSelection).getComposedRanges
  if (typeof fn !== 'function') return null
  for (const args of [[{ shadowRoots }], shadowRoots]) {
    try {
      const range = fn.apply(sel, args)[0]
      if (range != null) return range
    } catch {
      // 다음 형태로 재시도
    }
  }
  return null
}

/**
 * copy 를 직접 처리할 선택 범위. null 이면 처리하지 않고 기본 복사에 맡긴다.
 *
 * bodyIsolation 'shadow-dom' 에서는 본문이 `.htmlMailBody` host 의 shadowRoot 안이라 document selection 의
 * anchor/focus·getRangeAt 이 host 로 retarget 된다. getComposedRanges 로 실제 범위를 얻고, 본문 shadow
 * 경계를 걸친 선택은 기본 복사로 넘긴다. getComposedRanges 가 없는 runtime 에서 선택이 shadow 본문에
 * 닿으면 실제 범위를 알 수 없으므로 기본 복사로 넘긴다 — 텍스트·서식은 남고 인라인 이미지만 빠진다.
 * shadow 본문이 없으면 기존 판정(isSelectionInsideBody + getRangeAt(0)) 그대로다.
 */
export function resolveBodyCopyRange(sel: Selection, containers: HTMLElement[]): Range | null {
  if (sel.rangeCount === 0) return null
  const hosts = containers
    .flatMap((c) => [c, ...Array.from(c.querySelectorAll<HTMLElement>('.htmlMailBody'))])
    .filter((host) => host.shadowRoot != null)
  if (hosts.length === 0) return isSelectionInsideBody(sel, containers) ? sel.getRangeAt(0) : null

  const composed = readComposedRange(
    sel,
    hosts.map((host) => host.shadowRoot!),
  )
  if (composed != null) {
    const { startContainer, endContainer } = composed
    const inside = (node: Node) => containers.some((c) => composedContains(c, node))
    if (!inside(startContainer) || !inside(endContainer)) return null
    if (startContainer.getRootNode() !== endContainer.getRootNode()) return null
    const range = (startContainer.ownerDocument ?? document).createRange()
    range.setStart(startContainer, composed.startOffset)
    range.setEnd(endContainer, composed.endOffset)
    return range
  }

  if (!isSelectionInsideBody(sel, containers)) return null
  const range = sel.getRangeAt(0)
  return hosts.some((host) => range.intersectsNode(host)) ? null : range
}

export interface SerializeInlineImagesOptions {
  /** 차단 해제된 외부 이미지(`owa-src`) 복원 시 http→https 승격 등에 사용. */
  upgradeInsecureUrl?: (url: string) => string
}

/**
 * 인라인 이미지 마커 속성 목록.
 *   - owa-cid: cid 미해석 백업 (cid: 이미지의 cid 값 저장)
 *   - owa-src: 외부 이미지 차단 백업 (차단 해제 시 복원할 원본 src)
 *   - owa-srcset: 외부 이미지 차단 백업 (차단 해제 시 복원할 원본 srcset)
 *   - data-cid: cid 이미지 백업 마커
 * 복사 후 클립보드 HTML 에 남으면 안 되는 뷰어 내부 전용 속성이기도 하다.
 */
export const INLINE_MARKER_ATTRS = ['owa-cid', 'owa-src', 'owa-srcset', 'data-cid'] as const

/** 복사 후 클립보드 HTML 에 남으면 안 되는 뷰어 내부 전용 img 속성. INLINE_MARKER_ATTRS 와 동일 목록. */
const INTERNAL_IMG_ATTRS = INLINE_MARKER_ATTRS

/**
 * clone 된 선택 fragment 내 각 `<img>` 의 src 를 정리한다.
 *   1. `blob:` src 가 맵에 있으면 `data:` URL 로 치환.
 *   2. 맵 미스 + `owa-src` 보유(차단 외부 이미지) → `owa-src` 로 복원(선택적 https 승격).
 *   3. 그 외(이미 `http(s):`/`data:`, 또는 맵 미스인 `blob:`) → src 유지.
 * 마지막에 내부 전용 속성을 제거한다.
 */
export function serializeInlineImages(
  root: HTMLElement,
  urlToDataUrl: Record<string, string>,
  options: SerializeInlineImagesOptions = {},
): void {
  root.querySelectorAll('img').forEach((img) => {
    const src = img.getAttribute('src') ?? ''
    const dataUrl = urlToDataUrl[src]
    if (dataUrl != null) {
      img.setAttribute('src', dataUrl)
    } else {
      const owaSrc = img.getAttribute('owa-src')
      if (owaSrc != null && owaSrc.length > 0) {
        const restored =
          options.upgradeInsecureUrl != null ? options.upgradeInsecureUrl(owaSrc) : owaSrc
        img.setAttribute('src', restored)
        const owaSrcset = img.getAttribute('owa-srcset')
        if (owaSrcset != null) img.setAttribute('srcset', owaSrcset)
      }
    }
    INTERNAL_IMG_ATTRS.forEach((attr) => img.removeAttribute(attr))
  })
}

/**
 * img 가 인라인 마커 속성(owa-cid/owa-src/owa-srcset/data-cid) 중 하나라도 보유하는지 판별.
 * 마커 보유 img 는 serializeInlineImages 가 별도로 처리(owa-src 복원 등)하므로,
 * 변환 대상 스킴이면 변환 대상에 포함하고, 아니면 기존 복원 로직에 위임한다.
 */
export function isInlineMarkedImg(img: HTMLImageElement): boolean {
  return INLINE_MARKER_ATTRS.some((attr) => img.hasAttribute(attr))
}

/**
 * OMD-193 predicate 주입의 web 기본값: `blob:` 스킴만 변환 대상.
 * host(desktop)가 NineNative.isInlineImageConvertible 을 주입하면 그 predicate 로 대체된다
 * (desktop 인라인 이미지는 `officemail-inline://` 커스텀 프로토콜 — 창 간/앱 외부에서 해석 불가라 변환 필요).
 */
export const isBlobInlineImageSrc = (src: string | null | undefined): boolean =>
  src != null && /^blob:/i.test(src)

/**
 * 컨테이너 안에서 data URL 변환이 필요한 인라인 이미지 src 목록을 중복 없이 수집한다.
 *
 * 변환 대상 조건 (isConvertible 스킴 판별 통과분에 한정 — 기본 blob:, host 주입 시 대체):
 *   A. src 가 trackedUrls(inlineImgList.url 집합) 에 포함 — 추적된 인라인 이미지
 *   B. 마커 속성(isInlineMarkedImg) 보유                — cid/외부이미지 백업 경로
 *
 * A ∪ B 에 해당하지 않는 변환 대상 스킴 src 는 변환하지 않고 console.warn 로깅만 한다
 * (성능·안전: 출처 불명 src 를 데이터 직렬화 대상에서 제외).
 *
 * `trustConvertible=true` (host 가 네이티브 인라인 변환기를 주입한 경우 — OMD-193): host 의
 * `isInlineImageConvertible` 은 자기 소유 커스텀 스킴(`officemail-inline://`)만 통과시키는 정밀
 * predicate 이므로 통과 자체가 출처 보증이다 — 이때 A/B 게이트를 건너뛴다. 수신 메일은 Rust 가
 * 뷰어 렌더 전 `cid:`→`officemail-inline://` 치환을 끝내 `inlineImgList`(trackedUrls)·`owa-cid`
 * 마커 어느 쪽에도 안 잡혀(가설③), 게이트를 그대로 두면 복사 시 인라인 이미지가 소실된다.
 * web 기본(generic `blob:`)은 `trustConvertible=false` 로 엄격 게이트를 유지한다(무회귀).
 */
export function collectConvertibleImgSrcs(
  container: HTMLElement,
  trackedUrls: ReadonlySet<string>,
  isConvertible: (src: string | null | undefined) => boolean = isBlobInlineImageSrc,
  trustConvertible = false,
): string[] {
  const srcs = new Set<string>()
  container.querySelectorAll('img').forEach((img) => {
    const src = img.getAttribute('src')
    if (src == null || !isConvertible(src)) return
    const isTracked = trackedUrls.has(src)
    const isMarked = isInlineMarkedImg(img as HTMLImageElement)
    if (trustConvertible || isTracked || isMarked) {
      srcs.add(src)
    } else {
      console.warn(
        '[copyInlineImages] 변환 대상 스킴 src 가 추적 목록·마커 속성 어느 쪽에도 없음 — 변환 제외:',
        src,
      )
    }
  })
  return [...srcs]
}
